/**
 * UI-side equivalent of the backend `getEquivalentSynonymsOfQueryPhrase` flow.
 *
 * Bundles `synonym-mappings.json` (kept in sync with the backend's
 * `api/src/main/resources/search-v2/synonym-mappings.json`) and exposes
 * `getEquivalentSynonyms(query)` so the highlighter can derive synonyms
 * for a phrase without a backend round-trip.
 *
 * Each entry in the JSON looks like:
 *   { "mappingType": "equivalent", "synonyms": ["0%", "0 %", "zero percent", ...] }
 *
 * Behaviour mirrors backend logic: for a given query, every synonym group
 * that contains the query (case-insensitive, trimmed) contributes its other
 * members to the result. The query itself is filtered out so callers can
 * combine `[query, ...synonyms]` cleanly.
 */
import rawSynonymMappings from './synonym-mappings.json'

const safeLower = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : ''

// Defensive sanitiser: some legacy entries in synonym-mappings.json had
// regex-escaped parens / brackets baked into the source string (e.g.
// "\\(ten percent\\)" rather than "(ten percent)"). Stored as-is, those
// escapes prevent the highlighter regex from ever matching the real document
// text, because we'd add a SECOND layer of escaping on top. This step strips
// the leading backslash from any escaped regex meta-character so the data is
// always treated as a literal phrase. Safe to keep even after the JSON is
// cleaned — it's a no-op on already-clean entries.
const REGEX_METACHARS = new Set([
  '\\',
  '(',
  ')',
  '[',
  ']',
  '{',
  '}',
  '.',
  '*',
  '+',
  '?',
  '^',
  '$',
  '|',
])
const stripRegexEscapes = (value) => {
  if (typeof value !== 'string' || !value.includes('\\')) return value
  let out = ''
  for (let i = 0; i < value.length; i++) {
    if (value[i] === '\\' && i + 1 < value.length && REGEX_METACHARS.has(value[i + 1])) {
      out += value[i + 1]
      i++
    } else {
      out += value[i]
    }
  }
  return out
}

// Precompute a phrase → Set<synonym> index once at module load.
// Using lowercase keys keeps the lookup case-insensitive.
const SYNONYM_INDEX = (() => {
  const index = new Map()
  if (!Array.isArray(rawSynonymMappings)) return index

  for (const entry of rawSynonymMappings) {
    if (!entry || !Array.isArray(entry.synonyms)) continue
    const members = entry.synonyms
      .filter((s) => typeof s === 'string' && s.trim().length > 0)
      .map((s) => stripRegexEscapes(s.trim()))
    if (members.length === 0) continue

    for (const member of members) {
      const key = member.toLowerCase()
      let bucket = index.get(key)
      if (!bucket) {
        bucket = new Set()
        index.set(key, bucket)
      }
      for (const other of members) bucket.add(other)
    }
  }
  return index
})()

/**
 * @param {string} query
 * @returns {string[]} unique equivalent synonyms (excluding the query itself, case-insensitive)
 */
export const getEquivalentSynonyms = (query) => {
  const normalized = safeLower(query)
  if (!normalized) return []

  const bucket = SYNONYM_INDEX.get(normalized)
  if (!bucket) return []

  const result = []
  for (const candidate of bucket) {
    if (candidate.toLowerCase() === normalized) continue
    result.push(candidate)
  }
  return result
}
